/**
 * Super-admin preview from country-templates / DualCTA editors.
 *
 * Production truth: `?country=XX` alone is SPA fallback only.
 * Correct edge sim requires geo_sim=1 + gst (minted) + Clean VPN.
 * See docs/GEO_SIM_SUPER_ADMIN_PROMPT.md.
 */

import { trackGeoSimEvent, GEO_SIM_EVENTS } from "./analytics";
import { markGeoSimEligible } from "./boot";
import { expectedTemplateForCountry } from "./expectedTemplate";
import { mintGeoSimTokenFromApi } from "./mint";
import {
  buildGeoSimUrl,
  writeGeoSimSession,
  type BuildGeoSimUrlInput,
} from "./session";
import type { GeoSimSession, GeoSimVpnMode } from "./types";

export type OpenAdminCountryPreviewInput = {
  country: string;
  /** Landing path. Home `/` for DualCTA/Base. */
  path?: string;
  /**
   * Clerk auth headers (Authorization: Bearer …).
   * Prefer this on anaa — same shape as useAdminAuth().getAuthHeaders.
   */
  getAuthHeaders: () => Promise<Record<string, string>> | Record<string, string>;
  /** Default true — admin stays on panel, visitor tab opens. */
  openInNewTab?: boolean;
  province?: string;
  city?: string;
  state?: string;
  lang?: string;
  simulateVpn?: GeoSimVpnMode;
  /** Optional source label for analytics (e.g. country_templates). */
  source?: string;
};

export type OpenAdminCountryPreviewResult =
  | {
      ok: true;
      url: string;
      country: string;
      expectedTemplate: string;
    }
  | { ok: false; error: string; code?: string };

/**
 * Mint gst, persist GeoSim session, open hard-nav URL with edge override.
 * Same contract as delfuocampo openAdminCountryGeoSimPreview, adapted to
 * anaafernandez mint (token-only HMAC; country travels in query params).
 */
export async function openAdminCountryGeoSimPreview(
  input: OpenAdminCountryPreviewInput,
): Promise<OpenAdminCountryPreviewResult> {
  const country = (input.country || "").trim().toUpperCase().slice(0, 2);
  if (!/^[A-Z]{2}$/.test(country)) {
    return { ok: false, error: "País ISO-2 inválido" };
  }

  const path =
    input.path && input.path.startsWith("/") ? input.path : "/";
  const simulateVpn: GeoSimVpnMode = input.simulateVpn || "clean";
  const province =
    country === "AR" && input.province?.trim()
      ? input.province.trim()
      : null;
  const city = input.city?.trim() || null;
  const state =
    country === "US" && input.state?.trim()
      ? input.state.trim().toUpperCase()
      : null;
  const lang = input.lang?.trim() || null;

  const mint = await mintGeoSimTokenFromApi(input.getAuthHeaders);
  if (!mint.ok) {
    trackGeoSimEvent(GEO_SIM_EVENTS.activateError, {
      error: mint.error,
      code: mint.code,
      http_status: mint.status,
      simulated_country: country,
      source: input.source || "admin_country_preview",
    });
    const hint =
      mint.code === "GEO_SIM_SECRET_MISSING"
        ? " · Configurá GEO_SIM_SECRET en Railway + Worker (mismo valor)"
        : mint.status === 401 || mint.status === 403
          ? " · Rol super_admin o QA requerido"
          : "";
    return {
      ok: false,
      error: (mint.error || "Mint failed") + hint,
      code: mint.code,
    };
  }

  const session: GeoSimSession = {
    country,
    state,
    province,
    city,
    lang,
    forceVariant: null,
    simulateVpn,
    simulateIp: null,
    token: mint.token,
    activatedAt: Date.now(),
  };
  writeGeoSimSession(session);
  markGeoSimEligible();

  const urlInput: BuildGeoSimUrlInput = {
    path,
    country,
    token: mint.token,
    state,
    province,
    city,
    lang,
    simulateVpn,
  };
  const url = buildGeoSimUrl(urlInput);
  const expected = expectedTemplateForCountry(country);

  trackGeoSimEvent(GEO_SIM_EVENTS.activate, {
    simulated_country: country,
    simulated_province: province,
    simulated_city: city,
    simulated_state: state,
    simulate_vpn: simulateVpn,
    expected_template: expected.templateName,
    source: input.source || "admin_country_preview",
    path,
  });

  if (typeof window !== "undefined") {
    if (input.openInNewTab !== false) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.assign(url);
    }
  }

  return {
    ok: true,
    url,
    country,
    expectedTemplate: expected.templateName,
  };
}
